// frontend/src/pages/staff/StaffBookingImport.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import StaffSidebarLayout from "./StaffSidebarLayout";
import "./StaffBookingImport.css";

function StaffBookingImport() {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(1); // 1: upload, 2: preview/mapping, 3: results
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [savedMapping, setSavedMapping] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [loadingMapping, setLoadingMapping] = useState(false);
  const navigate = useNavigate();

  // Load saved mapping from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("staffBookingImportMapping");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSavedMapping(parsed);
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      (selectedFile.type === "text/csv" || selectedFile.name.endsWith(".csv"))
    ) {
      setFile(selectedFile);
      setError("");
      setStep(1);
      setPreviewData(null);
      setResults(null);
    } else {
      setError("Please select a valid CSV file");
      setFile(null);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    try {
      setImporting(true);
      setError("");
      setLoadingMapping(true);

      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${apiBaseUrl}/api/staff/bookings/import/preview`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setPreviewData(response.data);
      // Prefer saved mapping if available, otherwise use suggested
      if (savedMapping && Object.keys(savedMapping).length > 0) {
        setColumnMapping(savedMapping);
      } else {
        setColumnMapping(response.data.suggestedMapping || {});
      }
      setStep(2);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to preview file. Please try again.",
      );
    } finally {
      setImporting(false);
      setLoadingMapping(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    try {
      setImporting(true);
      setError("");

      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("columnMapping", JSON.stringify(columnMapping));

      const response = await axios.post(
        `${apiBaseUrl}/api/staff/bookings/import`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setResults(response.data);
      setStep(3);
      setFile(null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to import bookings. Please try again.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleMappingChange = (ourField, csvColumn) => {
    setColumnMapping((prev) => ({
      ...prev,
      [ourField]: csvColumn,
    }));
  };

  const handleStartOver = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setColumnMapping({});
    setResults(null);
    setError("");
    if (document.getElementById("file-input")) {
      document.getElementById("file-input").value = "";
    }
  };

  const handleSaveMapping = () => {
    localStorage.setItem(
      "staffBookingImportMapping",
      JSON.stringify(columnMapping),
    );
    setSavedMapping(columnMapping);
  };

  const handleClearSavedMapping = () => {
    localStorage.removeItem("staffBookingImportMapping");
    setSavedMapping(null);
  };

  const downloadTemplate = () => {
    const template = [
      "Customer Name,Customer Email,Customer Phone,Service,Date,Time,Duration (min),Notes,Status",
      "John Doe,john@example.com,555-1234,Haircut,2026-02-15,10:00,60,First time customer,confirmed",
      "Jane Smith,jane@example.com,555-5678,Color,2026-02-16,14:00,90,,confirmed",
    ].join("\n");

    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "booking_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Field definitions
  const fieldDefinitions = {
    customerName: {
      label: "Customer Name*",
      description: "Full name of the customer",
    },
    customerEmail: {
      label: "Customer Email*",
      description: "Customer email address",
    },
    customerPhone: {
      label: "Customer Phone",
      description: "Phone number (optional)",
    },
    appointmentDate: {
      label: "Appointment Date*",
      description: "Date in YYYY-MM-DD format (e.g., 2026-01-15)",
    },
    startTime: {
      label: "Start Time*",
      description: "Time in HH:MM format (e.g., 14:30)",
    },
    endTime: {
      label: "End Time",
      description: "Optional if Duration provided",
    },
    duration: {
      label: "Duration (minutes)",
      description: "Used to calculate end time if missing",
    },
    service: {
      label: "Service Name",
      description:
        "Service name - will be auto-created if not in your profile",
    },
    notes: { label: "Notes", description: "Customer notes (optional)" },
    status: {
      label: "Status",
      description: "confirmed, pending, completed, or cancelled",
    },
  };

  return (
    <StaffSidebarLayout roleType="professional">
      <div className="staff-booking-import-page">
        <div className="import-container">
          <div className="import-header">
            <div>
              <h1>Import Appointments</h1>
              <p className="subtitle">
                {step === 1 && "Upload your CSV file to import past appointments"}
                {step === 2 && "Review and confirm column mapping"}
                {step === 3 && "Import complete"}
              </p>
            </div>
            {step === 1 && (
              <button
                onClick={downloadTemplate}
                className="btn-download-template"
                title="Download CSV template with required columns"
              >
                📥 Download Template
              </button>
            )}
          </div>

        {/* Step Indicator */}
        <div className="step-indicator">
          <div
            className={`step ${step >= 1 ? "active" : ""}`}
            style={{
              padding: "8px 16px",
              borderRadius: "20px",
              backgroundColor: step >= 1 ? "#0ea5e9" : "#e5e7eb",
              color: step >= 1 ? "white" : "#666",
            }}
          >
            1. Upload
          </div>
          <div
            className="step-connector"
            style={{
              flex: 1,
              height: "2px",
              backgroundColor: step >= 2 ? "#0ea5e9" : "#e5e7eb",
            }}
          />
          <div
            className={`step ${step >= 2 ? "active" : ""}`}
            style={{
              padding: "8px 16px",
              borderRadius: "20px",
              backgroundColor: step >= 2 ? "#0ea5e9" : "#e5e7eb",
              color: step >= 2 ? "white" : "#666",
            }}
          >
            2. Map
          </div>
          <div
            className="step-connector"
            style={{
              flex: 1,
              height: "2px",
              backgroundColor: step >= 3 ? "#0ea5e9" : "#e5e7eb",
            }}
          />
          <div
            className={`step ${step >= 3 ? "active" : ""}`}
            style={{
              padding: "8px 16px",
              borderRadius: "20px",
              backgroundColor: step >= 3 ? "#0ea5e9" : "#e5e7eb",
              color: step >= 3 ? "white" : "#666",
            }}
          >
            3. Complete
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="error-box"
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
              color: "#c00",
            }}
          >
            {error}
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="step-content">
            <div
              style={{
                border: "2px dashed #0ea5e9",
                borderRadius: "8px",
                padding: "40px 20px",
                textAlign: "center",
                marginBottom: "20px",
                backgroundColor: "#f0f9ff",
              }}
            >
              <div style={{ marginBottom: "15px" }}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0ea5e9"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
              </div>
              <h3>Upload CSV File</h3>
              <p style={{ color: "#666", marginBottom: "15px" }}>
                Drag and drop or click to select
              </p>
              <input
                id="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                onClick={() => document.getElementById("file-input").click()}
                className="btn-primary"
              >
                Choose File
              </button>
            </div>

            {file && (
              <div
                style={{
                  background: "#f3f4f6",
                  padding: "12px",
                  borderRadius: "6px",
                  marginBottom: "20px",
                }}
              >
                <strong>Selected:</strong> {file.name}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={handlePreview}
                disabled={!file || importing}
                className="btn-primary"
              >
                {importing ? "Processing..." : "Continue"}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Map Columns */}
        {step === 2 && previewData && (
          <div className="step-content">
            <div style={{ marginBottom: "20px" }}>
              <h3>Column Mapping</h3>
              <p style={{ color: "#666", marginBottom: "20px" }}>
                Match your CSV columns to appointment fields
              </p>

              {/* Required Fields */}
              <div style={{ marginBottom: "30px" }}>
                <h4 style={{ color: "#333", marginBottom: "15px" }}>
                  Required Fields
                </h4>
                {Object.entries(fieldDefinitions)
                  .filter(
                    ([key]) =>
                      key.includes("customer") ||
                      key.includes("appointmentDate") ||
                      key.includes("Time"),
                  )
                  .map(([field, def]) => (
                    <div key={field} style={{ marginBottom: "15px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontWeight: "500",
                        }}
                      >
                        {def.label}
                      </label>
                      <p style={{ color: "#666", fontSize: "12px", marginBottom: "8px" }}>
                        {def.description}
                      </p>
                      <select
                        value={columnMapping[field] || ""}
                        onChange={(e) =>
                          handleMappingChange(field, e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "8px",
                          borderRadius: "6px",
                          border: "1px solid #ddd",
                        }}
                      >
                        <option value="">-- Select Column --</option>
                        {previewData.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
              </div>

              {/* Optional Fields */}
              <div>
                <h4 style={{ color: "#333", marginBottom: "15px" }}>
                  Optional Fields
                </h4>
                {Object.entries(fieldDefinitions)
                  .filter(
                    ([key]) =>
                      !key.includes("customer") &&
                      !key.includes("appointmentDate") &&
                      !key.includes("Time"),
                  )
                  .map(([field, def]) => (
                    <div key={field} style={{ marginBottom: "15px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontWeight: "500",
                        }}
                      >
                        {def.label}
                      </label>
                      <p style={{ color: "#666", fontSize: "12px", marginBottom: "8px" }}>
                        {def.description}
                      </p>
                      <select
                        value={columnMapping[field] || ""}
                        onChange={(e) =>
                          handleMappingChange(field, e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "8px",
                          borderRadius: "6px",
                          border: "1px solid #ddd",
                        }}
                      >
                        <option value="">-- Select Column --</option>
                        {previewData.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
              </div>
            </div>

            {/* Mapping Save/Load */}
            <div
              style={{
                background: "#f9fafb",
                padding: "15px",
                borderRadius: "6px",
                marginBottom: "20px",
              }}
            >
              <div style={{ fontSize: "12px", color: "#666" }}>
                <p>
                  Save your column mapping to reuse it for future imports.
                </p>
                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                  <button
                    onClick={handleSaveMapping}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      background: "#e5e7eb",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    💾 Save Mapping
                  </button>
                  {savedMapping && (
                    <button
                      onClick={handleClearSavedMapping}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        background: "#fee2e2",
                        border: "1px solid #fca5a5",
                        borderRadius: "4px",
                        cursor: "pointer",
                        color: "#dc2626",
                      }}
                    >
                      ✕ Clear Saved
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Data Preview */}
            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ marginBottom: "10px" }}>Data Preview</h4>
              <div
                style={{
                  overflowX: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      {previewData.headers.map((header) => (
                        <th
                          key={header}
                          style={{
                            padding: "8px",
                            textAlign: "left",
                            borderBottom: "1px solid #e5e7eb",
                            fontWeight: "600",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.preview.map((row, idx) => (
                      <tr key={idx}>
                        {previewData.headers.map((header) => (
                          <td
                            key={`${idx}-${header}`}
                            style={{
                              padding: "8px",
                              borderBottom: "1px solid #f3f4f6",
                              color: "#666",
                            }}
                          >
                            {row[header] || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <button
                onClick={handleStartOver}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleImport}
                disabled={importing}
                className="btn-primary"
              >
                {importing ? "Importing..." : "Import Appointments"}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && results && (
          <div className="step-content">
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "8px",
                padding: "20px",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ color: "#16a34a", margin: "0 0 15px 0" }}>
                ✓ Import Complete
              </h3>
              <div style={{ color: "#166534" }}>
                <p>
                  <strong>Imported:</strong> {results.summary.imported}{" "}
                  appointments
                </p>
                {results.summary.failed > 0 && (
                  <p>
                    <strong>Failed:</strong> {results.summary.failed} rows
                  </p>
                )}
                {results.summary.duplicates > 0 && (
                  <p style={{ color: "#92400e" }}>
                    <strong>Already imported:</strong> {results.summary.duplicates} skipped
                  </p>
                )}
              </div>
            </div>

            {/* Duplicate Notices */}
            {results.duplicateNotices && results.duplicateNotices.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ marginBottom: "10px", color: "#92400e" }}>
                  ℹ️ {results.duplicateNotices.length} already imported (skipped)
                </h4>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    background: "#fffbeb",
                    border: "1px solid #fcd34d",
                    borderRadius: "6px",
                    padding: "10px",
                  }}
                >
                  {results.duplicateNotices.map((notice, idx) => (
                    <div key={idx} style={{ marginBottom: "6px", fontSize: "12px", color: "#78350f" }}>
                      <strong>Row {notice.row}:</strong> {notice.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {results.errors.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ marginBottom: "10px" }}>
                  ⚠ {results.errors.length} error(s)
                </h4>
                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                    padding: "10px",
                  }}
                >
                  {results.errors.map((err, idx) => (
                    <div key={idx} style={{ marginBottom: "8px", fontSize: "12px" }}>
                      <strong>Row {err.row}:</strong> {err.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button
                onClick={handleStartOver}
                className="btn-primary"
                style={{ background: "#0ea5e9" }}
              >
                Import More
              </button>
              <button
                onClick={() => navigate(-1)}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </StaffSidebarLayout>
  );
}

export default StaffBookingImport;
